import type { FacilityDto } from "@ckweb/ckfetch/connectables/setup";

/**
 * This composable is a wrapper around useFacilityStore
 */
export function useFacilityApi() {
  const { t } = useFormatOptions();
  const appStore = useAppStore();
  const { currentOrganization } = useOrganizationApi();
  const facilityStore = useFacilityStore();
  const subscriptionStore = useSubscriptionStore();
  const { createFacilityPath } = useFacilityRoute();

  const getFacility = useGetFacility();
  const getFacilityList = useGetFacilityList();
  const deleteFacility = useDeleteFacility();

  const {
    currentFacility,
    facilities,
    currentPage,
    currentPageSize,
    totalPages,
    searchPhrase,
    statusFilter,
    statuses,
  } = storeToRefs(facilityStore);

  const isLoadingCurrentFacility = computed(() => {
    return !currentFacility.value && appStore.activeFacilityId !== undefined;
  });

  const facilityCap = computed(() => {
    return currentOrganization.value?.subscription?.facilityCap;
  });

  const equipmentAreaCap = computed(() => {
    return currentOrganization.value?.subscription?.equipmentAreaCap;
  });

  const facilityCapReached = computed(() => {
    if (!facilityCap.value) return false;
    return facilities.value.length >= facilityCap.value;
  });

  const equipmentCapReached = computed(() => {
    if (!equipmentAreaCap.value) return false;
    return facilities.value.length >= equipmentAreaCap.value;
  });

  async function loadFacilities(
    defaults: {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: string;
      force?: boolean;
    } = {},
  ) {
    facilityStore.searchPhrase = defaults.search ?? facilityStore.searchPhrase;
    facilityStore.statusFilter = defaults.status ?? facilityStore.statusFilter;

    if (
      !defaults.force &&
      defaults.pageSize === facilityStore.currentPageSize &&
      defaults.page === facilityStore.currentPage
    ) {
      // No need to reload if the page and page size are the same
      return facilities.value;
    }
    facilityStore.currentPage = defaults.page ?? facilityStore.currentPage;
    facilityStore.currentPageSize =
      defaults.pageSize ?? facilityStore.currentPageSize;

    const data = await getFacilityList.safeFetch({
      page: facilityStore.currentPage,
      pageSize: facilityStore.currentPageSize,
    });

    if (!data) {
      return facilityStore.facilities;
    }

    // TODO: Generated Code seems to cast to FacilityDto[] but the response is actually a FacilityDtoListResultSet
    const payload = data as unknown as {
      data: FacilityDto[];
      totalPages: number;
    };
    facilityStore.setFacilities(payload.data);
    totalPages.value = payload.totalPages;
    return payload.data;
  }

  const deleteSingleFacility = (facility: FacilityDto) => {
    return deleteFacility.safeMutate(facility.id, {
      requestOptions: apiRequestOpts({
        headers: {
          [PLANT_ID_HEADER]: facility.id.toString(),
        },
      }),
      mapError: () =>
        `${t("cksetup.facilities.failedToDeleteFacility")} ${facility.name}.`,
    });
  };

  const deleteFacilities = async (payload: FacilityDto[]) => {
    const removedItems = $ck._.compact(
      await Promise.all(payload.map(deleteSingleFacility)),
    );

    const currentFacilities = facilities.value.filter(
      (facility) => !removedItems.some(({ id }) => id === facility.id),
    );
    if (removedItems.length) {
      appStore.addSuccessMessage(t("cksetup.facilities.deletedSuccessfully"));
    }
    facilityStore.setFacilities(currentFacilities);
    return removedItems;
  };

  const setFacilities = (facilities: FacilityDto[]) => {
    facilityStore.setFacilities(facilities);
  };

  const loadCurrentFacility = async (facilityId: number) => {
    if (appStore.activeFacilityId === facilityId) {
      return currentFacility.value;
    }
    appStore.activeFacilityId = facilityId;
    const data = await getFacility.safeFetch(facilityId);
    facilityStore.currentFacility = data;
    return data;
  };

  const setCurrentPage = (value: number) => {
    currentPage.value = value;
  };

  const clearCurrentFacility = async () => {
    appStore.activeFacilityId = undefined;
    currentFacility.value = undefined;
  };

  const refreshCurrentFacility = async () => {
    if (appStore.activeFacilityId) {
      return loadCurrentFacility(appStore.activeFacilityId);
    }
  };

  const navigateToFacilityCreation = () => {
    if (facilityCapReached.value) {
      subscriptionStore.isUpgradeConfirmationShown = true;
      return;
    }

    clearCurrentFacility();
    return navigateTo(
      createFacilityPath({
        wizardStep: "blank",
      }),
    );
  };

  return {
    currentPageSize,
    currentPage: readonly(currentPage),
    totalPages: readonly(totalPages),
    searchPhrase: readonly(searchPhrase),
    statusFilter: readonly(statusFilter),
    statuses: readonly(statuses),
    // this need to be computed or typescript will complain about the type
    facilities: computed(() => facilities.value),
    currentFacility: computed(() => currentFacility.value),
    activeFacilityId: computed(() => appStore.activeFacilityId),
    isLoadingCurrentFacility: readonly(isLoadingCurrentFacility),
    facilityCap,
    facilityCapReached,
    equipmentCapReached,
    loadCurrentFacility,
    loadFacilities,
    deleteFacilities,
    setFacilities,
    setCurrentPage,
    refreshCurrentFacility,
    clearCurrentFacility,
    navigateToFacilityCreation,
  };
}
